import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useState } from 'react'
import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  Receipt,
  ShieldCheck,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import {
  adminGetSystemStats,
  adminListFeatureCatalog,
} from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { adminGetPlatformRevenueMetrics } from '../../gen/cortado/v1/admin-AdminService_connectquery'
import type { RevenueTrend, StatTrend } from '../../gen/cortado/v1/admin_pb'
import { StatCard } from '../../components/stat-card'
import { formatUsdCents } from '../../lib/billing'
import { deadFeatureRows, featureDriftSummary, featureLabel } from '../../lib/feature-catalog'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'

export const Route = createFileRoute('/_admin/dashboard')({
  component: AdminDashboard,
})

const BOOKED_MRR_TITLE =
  'Includes trialing, grace, and paused-payment orgs at full booked value — not collected revenue. Comp accounts are excluded.'

function trend(t?: StatTrend) {
  if (!t) return undefined
  return { current: t.last30Days, previous: t.previous30Days }
}

function revenueTrend(r?: RevenueTrend) {
  if (!r) return undefined
  return {
    current: Number(r.last30DaysCents),
    previous: Number(r.previous30DaysCents),
    format: 'currency' as const,
  }
}

function arrTrend(r?: RevenueTrend) {
  if (!r) return undefined
  return {
    current: Number(r.last30DaysCents) * 12,
    previous: Number(r.previous30DaysCents) * 12,
    format: 'currency' as const,
  }
}

function AdminDashboard() {
  const { data, isLoading } = useQuery(adminGetSystemStats, {})
  const {
    data: revenueData,
    isLoading: revenueLoading,
    isError: revenueError,
    error: revenueQueryError,
  } = useQuery(adminGetPlatformRevenueMetrics, {})
  const stats = data?.stats
  const metrics = revenueData?.metrics

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm">System-wide health</p>
      </div>

      <FeatureDriftCard />

      <section className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground">Tenant activity</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StatCard
            icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
            label="Organizations"
            value={isLoading ? '--' : (stats?.organizations?.total ?? 0).toLocaleString()}
            valueLabel={isLoading ? undefined : 'total'}
            delta={trend(stats?.organizations)}
          />
          <StatCard
            icon={<Users className="h-4 w-4 text-muted-foreground" />}
            label="Users"
            value={isLoading ? '--' : (stats?.users?.total ?? 0).toLocaleString()}
            valueLabel={isLoading ? undefined : 'total'}
            delta={trend(stats?.users)}
          />
          <StatCard
            icon={<FileText className="h-4 w-4 text-muted-foreground" />}
            label="Leads"
            value={isLoading ? '--' : (stats?.leads?.total ?? 0).toLocaleString()}
            valueLabel={isLoading ? undefined : 'total'}
            delta={trend(stats?.leads)}
          />
          <StatCard
            icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
            label="Events"
            value={isLoading ? '--' : (stats?.events?.total ?? 0).toLocaleString()}
            valueLabel={isLoading ? undefined : 'total'}
            delta={trend(stats?.events)}
          />
          <StatCard
            icon={<Receipt className="h-4 w-4 text-muted-foreground" />}
            label="Invoices"
            value={isLoading ? '--' : (stats?.invoices?.total ?? 0).toLocaleString()}
            valueLabel={isLoading ? undefined : 'total'}
            delta={trend(stats?.invoices)}
          />
          <StatCard
            icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
            label="Revenue"
            value={
              isLoading
                ? '--'
                : `$${(Number(stats?.revenue?.totalCents ?? 0n) / 100).toLocaleString()}`
            }
            valueLabel={isLoading ? undefined : 'invoice collections'}
            delta={revenueTrend(stats?.revenue)}
          />
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground">Platform billing</h2>
            <p className="text-muted-foreground text-xs" title={BOOKED_MRR_TITLE}>
              Cortado subscription MRR/ARR. Last 30 days vs previous 30.
            </p>
          </div>
          <Link to="/billing" className="text-sm font-medium text-primary hover:underline shrink-0">
            View billing
          </Link>
        </div>
        {revenueError ? (
          <p className="text-sm text-muted-foreground">
            {revenueQueryError instanceof Error
              ? revenueQueryError.message
              : 'Unable to load platform billing metrics'}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <StatCard
              icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
              label="Booked MRR"
              value={revenueLoading ? '--' : formatUsdCents(metrics?.bookedMrrCents ?? 0n)}
              valueLabel={revenueLoading ? undefined : 'current'}
              delta={revenueTrend(metrics?.netMrr)}
              detail={
                revenueLoading ? undefined : (
                  <MrrMovementDetail
                    newCents={metrics?.newMrr?.last30DaysCents}
                    expansionCents={metrics?.expansionMrr?.last30DaysCents}
                    contractionCents={metrics?.contractionMrr?.last30DaysCents}
                    churnedCents={metrics?.churnedMrr?.last30DaysCents}
                  />
                )
              }
            />
            <StatCard
              icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
              label="Booked ARR"
              value={revenueLoading ? '--' : formatUsdCents(metrics?.bookedArrCents ?? 0n)}
              valueLabel={revenueLoading ? undefined : 'current'}
              delta={arrTrend(metrics?.netMrr)}
            />
            <StatCard
              icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
              label="New subscriptions"
              value={
                revenueLoading
                  ? '--'
                  : (metrics?.recurringOrganizations ?? 0).toLocaleString()
              }
              valueLabel={revenueLoading ? undefined : 'recurring orgs'}
              delta={trend(metrics?.newSubscriptions)}
            />
            <StatCard
              icon={<ArrowUpRight className="h-4 w-4 text-muted-foreground" />}
              label="Upgrades"
              value={
                revenueLoading ? '--' : (metrics?.upgrades?.last30Days ?? 0).toLocaleString()
              }
              valueLabel={revenueLoading ? undefined : 'last 30 days'}
              delta={trend(metrics?.upgrades)}
            />
            <StatCard
              icon={<ArrowDownRight className="h-4 w-4 text-muted-foreground" />}
              label="Downgrades"
              value={
                revenueLoading ? '--' : (metrics?.downgrades?.last30Days ?? 0).toLocaleString()
              }
              valueLabel={revenueLoading ? undefined : 'last 30 days'}
              delta={trend(metrics?.downgrades)}
            />
          </div>
        )}
      </section>
    </div>
  )
}

export function FeatureDriftCard() {
  const [dismissed, setDismissed] = useState(false)
  const { data: catalogData } = useQuery(adminListFeatureCatalog, {})
  const rows = deadFeatureRows(catalogData?.entries ?? [])

  if (dismissed || !rows.length) return null

  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">{featureDriftSummary(rows)}</p>
            <p className="text-xs text-muted-foreground">
              {rows.map((row) => `${featureLabel(row.feature)} (${row.enabledOrgCount})`).join(', ')}
            </p>
            <Link to="/feature-hygiene" className="text-xs font-medium text-primary hover:underline">
              Review feature hygiene
            </Link>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss"
          className="shrink-0"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  )
}

function MrrMovementDetail({
  newCents,
  expansionCents,
  contractionCents,
  churnedCents,
}: {
  newCents?: bigint
  expansionCents?: bigint
  contractionCents?: bigint
  churnedCents?: bigint
}) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>New {formatUsdCents(newCents ?? 0n)}</span>
      <span>Expansion {formatUsdCents(expansionCents ?? 0n)}</span>
      <span>Contraction {formatUsdCents(contractionCents ?? 0n)}</span>
      <span>Churned {formatUsdCents(churnedCents ?? 0n)}</span>
    </div>
  )
}
